import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, type LucideIcon } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Badge } from '@/ui/base/badge';
import { cn } from '@/nucleo/utilitarios';

interface ItemDeHub {
  titulo: string;
  descricao: string;
  icone: LucideIcon;
  /** Presente = card clicável, rota já construída. Ausente = card inerte ("fase futura"), nunca link morto. */
  para?: string;
}

interface SubHubLayoutProps {
  titulo: string;
  descricao: string;
  voltarPara: string;
  itens: ItemDeHub[];
}

/**
 * Grade de cartões para os hubs de "Parametrização" (ONDA-CA2). Um card só
 * vira `<Link>` quando a rota de destino já existe de verdade — o item sem
 * `para` fica inerte, com selo "Em construção", em vez de apontar pra uma
 * rota que ainda não tem tela (CLAUDE.md, "Proibido": rota órfã).
 */
export function SubHubLayout({ titulo, descricao, voltarPara, itens }: SubHubLayoutProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 mb-2"
          onClick={() => navigate(voltarPara)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>
        <PageHeader title={titulo} description={descricao} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {itens.map((item) => {
          const Icon = item.icone;
          const conteudo = (
            <Card
              className={cn(
                'h-full rounded-2xl transition-all',
                item.para ? 'hover:border-primary/40 hover:shadow-md' : 'opacity-60',
              )}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-base">{item.titulo}</CardTitle>
                  {!item.para && (
                    <Badge variant="outline" className="ml-auto shrink-0 text-xs">
                      Em construção
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">{item.descricao}</p>
              </CardContent>
            </Card>
          );

          if (!item.para) {
            return (
              <div key={item.titulo} aria-disabled="true">
                {conteudo}
              </div>
            );
          }

          return (
            <Link key={item.titulo} to={item.para} className="group">
              {conteudo}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
